/**
 * The `assetRef` widget: an asset picker like Unreal's. The field shows the
 * current asset's name; opening it lists the assets of the prop's kind (from
 * the widget environment, so the widget never reads the content index) with
 * a search box, keyboard navigation and None. Picking is one committed change.
 *
 * The field itself (`AssetPickerField`) takes any list of options, so the
 * Inspector's Skin row picks among a skin set's models with the same UI.
 */
import { useId, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import type { PropType } from "../../world/schema/propTypes";
import { ChevronDownIcon } from "../ui/icons";
import { useDismissOnOutsideClick } from "../ui/useDismissOnOutsideClick";
import { assetFolder, assetLabel, filterAssetOptions } from "./assetPickerModel";
import { FieldError, fieldAria } from "./fieldAria";
import { isMixed, MIXED_LABEL, MIXED_OPTION } from "./mixed";
import type { WidgetProps } from "./types";
import { useWidgetEnvironment } from "./WidgetEnvironment";
import type { AssetOption } from "./WidgetEnvironment";

/** The first row: clearing the reference. */
const NONE: AssetOption = { value: "", label: "None", detail: "" };

/** What the field shows while the selected entities reference different assets. */
const MIXED_ASSETS: AssetOption = { value: MIXED_OPTION, label: MIXED_LABEL, detail: "" };

export function AssetRefWidget({ id, prop, value, onChange, onCommit, error }: WidgetProps) {
  const { kinds } = prop as Extract<PropType, { type: "assetRef" }>;
  const options = useWidgetEnvironment().assetOptions(kinds);
  // Mixed matches no row, so the list opens with None highlighted and nothing current.
  const current = isMixed(value) ? MIXED_OPTION : typeof value === "string" ? value : null;

  const known = options.find((option) => option.value === current);
  const shown = isMixed(value)
    ? MIXED_ASSETS
    : current === null
      ? NONE
      : (known ?? fallbackOption(current));

  return (
    <AssetPickerField
      id={id}
      options={options}
      nullable
      current={current}
      shown={shown}
      disabled={prop.readonly === true}
      error={error}
      onPick={(path) => {
        onChange(path);
        onCommit();
      }}
    />
  );
}

export interface AssetPickerFieldProps {
  readonly id: string;
  readonly options: readonly AssetOption[];
  /** Offers None first, for a reference that can be empty. */
  readonly nullable: boolean;
  /** The value the list highlights as current, or null. */
  readonly current: string | null;
  /** What the closed field shows. */
  readonly shown: AssetOption;
  readonly disabled?: boolean;
  readonly error?: string | null | undefined;
  /** A chosen path, or null for None. */
  readonly onPick: (path: string | null) => void;
}

/** The picker field: the current option, and a searchable list of the others when opened. */
export function AssetPickerField({
  id,
  options,
  nullable,
  current,
  shown,
  disabled = false,
  error,
  onPick,
}: AssetPickerFieldProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useDismissOnOutsideClick(root, open, () => {
    setOpen(false);
  });

  const pick = (path: string | null) => {
    onPick(path);
    setOpen(false);
    trigger.current?.focus();
  };

  return (
    <span ref={root} className="kn-field kn-asset-picker">
      <button
        ref={trigger}
        id={id}
        type="button"
        className="kn-input kn-asset-picker__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        {...fieldAria(id, error)}
        onClick={() => {
          setOpen((wasOpen) => !wasOpen);
        }}
      >
        <span className="kn-asset-picker__name">{shown.label}</span>
        {shown.detail && <span className="kn-asset-picker__detail">{shown.detail}</span>}
        <ChevronDownIcon size={10} />
      </button>
      {open && (
        <AssetList
          options={options}
          nullable={nullable}
          current={current}
          onPick={pick}
          onCancel={() => {
            setOpen(false);
            trigger.current?.focus();
          }}
        />
      )}
      <FieldError id={id} error={error} />
    </span>
  );
}

interface AssetListProps {
  readonly options: readonly AssetOption[];
  readonly nullable: boolean;
  readonly current: string | null;
  readonly onPick: (path: string | null) => void;
  readonly onCancel: () => void;
}

/** The popover: a search combobox over a listbox, with the current asset highlighted. */
function AssetList({ options, nullable, current, onPick, onCancel }: AssetListProps) {
  const listId = useId();
  const [query, setQuery] = useState("");
  const rows = [...(nullable ? [NONE] : []), ...filterAssetOptions(options, query)];
  const initial = Math.max(
    0,
    rows.findIndex((row) => row.value === (current ?? "")),
  );
  const [active, setActive] = useState(initial);
  const highlighted = Math.min(active, rows.length - 1);

  const choose = (row: AssetOption) => {
    onPick(row.value === "" ? null : row.value);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const row = rows[highlighted];
    if (event.key === "ArrowDown") setActive(Math.min(rows.length - 1, highlighted + 1));
    else if (event.key === "ArrowUp") setActive(Math.max(0, highlighted - 1));
    else if (event.key === "Enter" && row) choose(row);
    else if (event.key === "Escape") onCancel();
    else return;
    event.preventDefault();
    // Escape closes the picker, not the dialog or tour behind it.
    event.stopPropagation();
  };

  return (
    <div className="kn-asset-picker__popover">
      <input
        type="search"
        className="kn-input kn-asset-picker__search"
        role="combobox"
        aria-label="Search assets"
        aria-expanded="true"
        aria-controls={listId}
        aria-activedescendant={`${listId}-${highlighted}`}
        placeholder="Search assets"
        // Focus moves into the popover when it opens, like a native select.
        autoFocus
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setActive(0);
        }}
        onKeyDown={onKeyDown}
      />
      <ul id={listId} role="listbox" aria-label="Assets" className="kn-asset-picker__list">
        {rows.map((row, index) => (
          <li
            key={row.value || "none"}
            id={`${listId}-${index}`}
            role="option"
            aria-selected={index === highlighted}
            data-current={row.value === (current ?? "") || undefined}
            className="kn-asset-picker__option"
            onMouseEnter={() => {
              setActive(index);
            }}
            onClick={() => {
              choose(row);
            }}
          >
            {row.thumbnail && <img className="kn-asset-picker__thumb" src={row.thumbnail} alt="" />}
            <span className="kn-asset-picker__text">
              <span className="kn-asset-picker__name">{row.label}</span>
              {row.detail && <span className="kn-asset-picker__detail">{row.detail}</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A stored path the catalog doesn't list (e.g. injected by a test) still shows, as is. */
function fallbackOption(path: string): AssetOption {
  return { value: path, label: assetLabel(path), detail: assetFolder(path) };
}
